'use client';

import { useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type SplitMode = 'every-page' | 'custom-ranges';

type SplitPdfSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
};

type SplitSegment = {
  start: number;
  end: number;
};

const splitModes: Array<{
  id: SplitMode;
  label: string;
  hint: string;
}> = [
  {
    id: 'every-page',
    label: 'Every page',
    hint: 'Create one PDF per page and download them as one ZIP file.',
  },
  {
    id: 'custom-ranges',
    label: 'Custom ranges',
    hint: 'Split the PDF into page ranges like 1-3, 4-6, or 8.',
  },
];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createZipFileName(segmentCount: number) {
  return `cleanor.app-split-pdf-${segmentCount}-files-${formatExportTimestamp(new Date())}.zip`;
}

function createSegmentFileName(segment: SplitSegment, digits: number) {
  const start = String(segment.start).padStart(digits, '0');
  const end = String(segment.end).padStart(digits, '0');

  if (segment.start === segment.end) {
    return `cleanor.app-split-pdf-page-${start}.pdf`;
  }

  return `cleanor.app-split-pdf-pages-${start}-${end}.pdf`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

async function readSplitPdfFile(file: File): Promise<SplitPdfSource> {
  try {
    const bytes = await file.arrayBuffer();
    const pdf = await PDFDocument.load(bytes);

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: pdf.getPageCount(),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  }
}

function buildEveryPageSegments(pageCount: number): SplitSegment[] {
  return Array.from({ length: pageCount }, (_, index) => ({
    start: index + 1,
    end: index + 1,
  }));
}

function parseCustomSegments(input: string, pageCount: number): SplitSegment[] {
  const trimmed = input.trim();

  if (!trimmed) {
    throw new Error('Enter page ranges like 1-3, 4-6, or 8.');
  }

  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    throw new Error('Enter at least one page range.');
  }

  const segments: SplitSegment[] = [];
  const usedPages = new Set<number>();

  for (const part of parts) {
    const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
    const singleMatch = part.match(/^(\d+)$/);

    let start = 0;
    let end = 0;

    if (rangeMatch) {
      start = Number(rangeMatch[1]);
      end = Number(rangeMatch[2]);
    } else if (singleMatch) {
      start = Number(singleMatch[1]);
      end = start;
    } else {
      throw new Error(`"${part}" is not a valid page range.`);
    }

    if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
      throw new Error(`"${part}" is outside the page count for this PDF.`);
    }

    if (start > end) {
      throw new Error(`"${part}" is reversed. Put the smaller page first.`);
    }

    for (let page = start; page <= end; page += 1) {
      if (usedPages.has(page)) {
        throw new Error(`Page ${page} is included more than once. Use non-overlapping ranges.`);
      }

      usedPages.add(page);
    }

    segments.push({ start, end });
  }

  return segments;
}

export default function BrowserSplitPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<SplitPdfSource | null>(null);
  const [activeMode, setActiveMode] = useState<SplitMode>('every-page');
  const [customRanges, setCustomRanges] = useState('1-2, 3-4');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultDocumentCount, setResultDocumentCount] = useState<number | null>(null);

  const plannedSegments = useMemo(() => {
    if (!source) {
      return [] as SplitSegment[];
    }

    try {
      return activeMode === 'every-page'
        ? buildEveryPageSegments(source.pageCount)
        : parseCustomSegments(customRanges, source.pageCount);
    } catch {
      return [] as SplitSegment[];
    }
  }, [activeMode, customRanges, source]);

  const activeModeCopy = splitModes.find((mode) => mode.id === activeMode) ?? null;

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
    setResultDocumentCount(null);
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      setSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      setSource(null);
      setErrorMessage('Choose one PDF file to split.');
      return;
    }

    try {
      const nextSource = await readSplitPdfFile(file);
      setSource(nextSource);
    } catch (error) {
      setSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    setSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function splitPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    let segments: SplitSegment[] = [];

    try {
      segments =
        activeMode === 'every-page'
          ? buildEveryPageSegments(source.pageCount)
          : parseCustomSegments(customRanges, source.pageCount);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Check the page ranges and try again.',
      );
      return;
    }

    if (!segments.length) {
      setErrorMessage('Create at least one output PDF first.');
      return;
    }

    setIsSplitting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'split-pdf' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF split right now.';
        throw new Error(message);
      }

      const sourcePdf = await PDFDocument.load(await source.file.arrayBuffer());
      const zip = new JSZip();
      const digits = String(source.pageCount).length;

      for (const segment of segments) {
        const splitDocument = await PDFDocument.create();
        const pageIndexes = Array.from(
          { length: segment.end - segment.start + 1 },
          (_, index) => segment.start - 1 + index,
        );
        const copiedPages = await splitDocument.copyPages(sourcePdf, pageIndexes);

        copiedPages.forEach((page) => {
          splitDocument.addPage(page);
        });

        const outputBytes = Uint8Array.from(await splitDocument.save());
        zip.file(createSegmentFileName(segment, digits), outputBytes);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const nextDownloadUrl = URL.createObjectURL(zipBlob);
      const nextDownloadName = createZipFileName(segments.length);

      resetExportState();
      downloadUrlRef.current = nextDownloadUrl;
      setResultSize(zipBlob.size);
      setResultDocumentCount(segments.length);
      triggerBrowserDownload(nextDownloadUrl, nextDownloadName);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Split PDF download started. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Split PDF download started. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Split PDF download started.',
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The PDF could not be split.');
    } finally {
      setIsSplitting(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Split one PDF into smaller documents</h2>
          <p>
            Upload one PDF, choose every page or custom ranges, and download the split result as a
            clean ZIP file without sending the document to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Split modes">
        {splitModes.map((mode) => (
          <button
            key={mode.id}
            className={`tool-live-route ${activeMode === mode.id ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setActiveMode(mode.id);
              setErrorMessage(null);
              setCreditMessage(null);
              resetExportState();
            }}
            type="button"
          >
            {mode.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source
                  ? `${source.pageCount} page${source.pageCount === 1 ? '' : 's'} ready to split`
                  : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} source size • ${plannedSegments.length || 0} split file${plannedSegments.length === 1 ? '' : 's'} planned`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {activeMode === 'custom-ranges' ? (
            <label className="field">
              <span>Page ranges</span>
              <input
                onChange={(event) => {
                  setCustomRanges(event.target.value);
                  setErrorMessage(null);
                  setCreditMessage(null);
                  resetExportState();
                }}
                placeholder="1-3, 4-6, 8"
                type="text"
                value={customRanges}
              />
              <small>Use commas between ranges. Example: 1-3, 5, 7-9</small>
            </label>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || !plannedSegments.length || isSplitting}
              onClick={() => void splitPdf()}
              type="button"
            >
              {isSplitting ? 'Splitting PDF…' : 'Split PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF splitter</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → ZIP</span>
            <span>Every page</span>
            <span>Custom ranges</span>
            <span>Local export</span>
          </div>
          <p>
            {activeModeCopy?.hint ??
              'Choose how the pages should be broken up, then export the split set as one ZIP file.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Use standard unlocked PDFs. Password-protected files are not supported here.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Mode</strong>
                <span>{activeMode === 'every-page' ? 'Every page' : 'Custom ranges'}</span>
              </div>
              <div>
                <strong>Output files</strong>
                <span>{plannedSegments.length || 0}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>ZIP size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {resultDocumentCount ? (
                <div>
                  <strong>Last export</strong>
                  <span>{`${resultDocumentCount} PDF file${resultDocumentCount === 1 ? '' : 's'}`}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Planned outputs</h3>
          <div className="tool-pdf-page-list">
            {plannedSegments.length ? (
              plannedSegments.map((segment, index) => (
                <article className="tool-pdf-page-card" key={`${segment.start}-${segment.end}`}>
                  <div className="tool-pdf-page-thumb tool-pdf-file-thumb">
                    <strong>
                      {segment.start === segment.end
                        ? `${segment.start}`
                        : `${segment.start}-${segment.end}`}
                    </strong>
                    <span>{segment.start === segment.end ? 'page' : 'range'}</span>
                  </div>
                  <div className="tool-pdf-page-copy">
                    <strong>
                      {segment.start === segment.end
                        ? `Output ${index + 1}: page ${segment.start}`
                        : `Output ${index + 1}: pages ${segment.start}-${segment.end}`}
                    </strong>
                    <span>
                      {segment.end - segment.start + 1} page
                      {segment.end - segment.start + 1 === 1 ? '' : 's'} in this PDF
                    </span>
                    <span>{createSegmentFileName(segment, String(source.pageCount).length)}</span>
                  </div>
                </article>
              ))
            ) : (
              <article className="tool-pdf-page-card">
                <div className="tool-pdf-page-thumb tool-pdf-file-thumb">
                  <strong>?</strong>
                  <span>range</span>
                </div>
                <div className="tool-pdf-page-copy">
                  <strong>Check the ranges before export</strong>
                  <span>Use values like 1-3, 5, 7-9 inside the current page count.</span>
                  <span>The split export will start after the ranges are valid.</span>
                </div>
              </article>
            )}
          </div>
        </div>
      ) : null}
    </article>
  );
}
